/**
 * Bulk Action Toolbar Component
 *
 * A toolbar that appears when items are selected, providing bulk actions
 * like approve, reject, and export.
 */

import { renderIcon } from '../utils/icons';
import { bulkSelectionStore } from '../services/bulk-selection-store';

export interface BulkActionConfig {
  onApprove?: (ids: string[]) => Promise<void>;
  onReject?: (ids: string[]) => Promise<void>;
  onExport?: (ids: string[]) => void;
  showApprove?: boolean;
  showReject?: boolean;
  showExport?: boolean;
  customActions?: Array<{
    label: string;
    icon?: string;
    className?: string;
    onClick: (ids: string[]) => void | Promise<void>;
  }>;
}

export class BulkActionToolbar {
  private container: HTMLElement;
  private config: BulkActionConfig;
  private isProcessing = false;
  private unsubscribe?: () => void;

  constructor(config: BulkActionConfig = {}) {
    this.config = {
      showApprove: true,
      showReject: true,
      showExport: true,
      ...config,
    };

    this.container = this.createElement();
    this.bindEvents();
  }

  private createElement(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'bulk-action-toolbar fixed bottom-6 left-1/2 -translate-x-1/2 z-50 opacity-0 translate-y-4 pointer-events-none transition-all duration-300';
    container.innerHTML = `
      <div class="flex items-center gap-3 px-4 py-3 bg-card border border-border rounded-lg shadow-lg backdrop-blur-sm">
        <div class="flex items-center gap-2">
          <span class="selected-count text-sm font-medium text-foreground">0 items selected</span>
          <button type="button" class="clear-selection text-sm text-muted-foreground hover:text-foreground transition-colors">
            Clear
          </button>
        </div>
        <div class="w-px h-6 bg-border"></div>
        <div class="action-buttons flex items-center gap-2">
          ${this.config.showApprove ? `
            <button type="button" class="bulk-approve-btn inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-success text-success-foreground rounded-md hover:bg-success/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              ${renderIcon('checkCircle', { size: 'sm' }).outerHTML}
              Approve
            </button>
          ` : ''}
          ${this.config.showReject ? `
            <button type="button" class="bulk-reject-btn inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              ${renderIcon('xCircle', { size: 'sm' }).outerHTML}
              Reject
            </button>
          ` : ''}
          ${this.config.showExport ? `
            <button type="button" class="bulk-export-btn inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors">
              ${renderIcon('download', { size: 'sm' }).outerHTML}
              Export CSV
            </button>
          ` : ''}
          ${this.config.customActions?.map(action => `
            <button type="button" class="custom-action-btn inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${action.className || 'bg-secondary text-secondary-foreground hover:bg-secondary/90'}" data-action="${action.label}">
              ${action.icon ? renderIcon(action.icon as any, { size: 'sm' }).outerHTML : ''}
              ${action.label}
            </button>
          `).join('') || ''}
        </div>
      </div>
    `;

    return container;
  }

  private bindEvents(): void {
    // Subscribe to selection changes
    this.unsubscribe = bulkSelectionStore.subscribe((selectedIds) => {
      this.updateDisplay(selectedIds);
    });

    // Clear selection button
    const clearBtn = this.container.querySelector('.clear-selection');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        bulkSelectionStore.deselectAll();
      });
    }

    // Approve button
    const approveBtn = this.container.querySelector('.bulk-approve-btn');
    if (approveBtn && this.config.onApprove) {
      approveBtn.addEventListener('click', async () => {
        if (this.isProcessing) return;
        await this.handleAction(this.config.onApprove!);
      });
    }

    // Reject button
    const rejectBtn = this.container.querySelector('.bulk-reject-btn');
    if (rejectBtn && this.config.onReject) {
      rejectBtn.addEventListener('click', async () => {
        if (this.isProcessing) return;
        await this.handleAction(this.config.onReject!);
      });
    }

    // Export button
    const exportBtn = this.container.querySelector('.bulk-export-btn');
    if (exportBtn && this.config.onExport) {
      exportBtn.addEventListener('click', () => {
        if (this.isProcessing) return;
        const selectedIds = Array.from(bulkSelectionStore.getSelectedIds());
        this.config.onExport!(selectedIds);
      });
    }

    // Custom action buttons
    this.config.customActions?.forEach(action => {
      const btn = this.container.querySelector(`.custom-action-btn[data-action="${action.label}"]`);
      if (btn) {
        btn.addEventListener('click', async () => {
          if (this.isProcessing) return;
          const selectedIds = Array.from(bulkSelectionStore.getSelectedIds());
          await action.onClick(selectedIds);
        });
      }
    });
  }

  private async handleAction(action: (ids: string[]) => Promise<void>): Promise<void> {
    this.isProcessing = true;
    this.setButtonsDisabled(true);

    try {
      const selectedIds = Array.from(bulkSelectionStore.getSelectedIds());
      await action(selectedIds);
      // Clear selection after successful action
      bulkSelectionStore.deselectAll();
    } catch (error) {
      console.error('Bulk action failed:', error);
      // Error handling is done in the action callback
    } finally {
      this.isProcessing = false;
      this.setButtonsDisabled(false);
    }
  }

  private setButtonsDisabled(disabled: boolean): void {
    const buttons = this.container.querySelectorAll('button');
    buttons.forEach(btn => {
      if (disabled) {
        btn.setAttribute('disabled', 'disabled');
      } else {
        btn.removeAttribute('disabled');
      }
    });
  }

  private updateDisplay(selectedIds: Set<string>): void {
    const count = selectedIds.size;
    const countElement = this.container.querySelector('.selected-count');
    if (countElement) {
      countElement.textContent = `${count} ${count === 1 ? 'item' : 'items'} selected`;
    }

    // Show/hide toolbar based on selection
    if (count > 0) {
      this.show();
    } else {
      this.hide();
    }
  }

  private show(): void {
    // Force a reflow to ensure the transition works
    this.container.offsetHeight;
    this.container.classList.remove('opacity-0', 'translate-y-4', 'pointer-events-none');
    this.container.classList.add('opacity-100', 'translate-y-0');
  }

  private hide(): void {
    this.container.classList.add('opacity-0', 'translate-y-4', 'pointer-events-none');
    this.container.classList.remove('opacity-100', 'translate-y-0');
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.container);
  }

  unmount(): void {
    this.container.remove();
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  destroy(): void {
    this.unmount();
  }
}